// Procedures — Phase 2e (queue, 2026-10-03) + Phase 3.c₂ (catalogue, 2026-10-04).
//
// Two segments: Queue (open service_requests of type 'procedure' across the
// clinic — nurse execution surface) and Catalogue (per-clinic procedure
// definitions + pricing; doctor/admin edit, others read). Mirrors
// LabsScreen so staff muscle memory carries over.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
    ClinicProcedure,
    ProceduresQueueEntry,
    TriageAcuity,
    UpsertClinicProcedureRequest,
} from '@pamosi/shared';
import { proceduresApi, procedureCatalogueApi, clinicApi } from '../lib/api';

type Segment = 'queue' | 'catalogue';

const ACUITY_CLASS: Record<TriageAcuity | 'none', string> = {
    routine: 'bg-slate-100 text-slate-600',
    urgent: 'bg-amber-100 text-amber-800',
    emergency: 'bg-rose-100 text-rose-800',
    none: 'bg-slate-50 text-slate-400',
};

const ACUITY_LABEL: Record<TriageAcuity | 'none', string> = {
    routine: 'Routine',
    urgent: 'Urgent',
    emergency: 'Emergency',
    none: 'No triage',
};

const STATUS_LABEL: Record<ProceduresQueueEntry['status'], string> = {
    requested: 'Requested',
    in_progress: 'In progress',
    fulfilled: 'Fulfilled',
    cancelled: 'Cancelled',
};

export function ProceduresQueueScreen() {
    const [segment, setSegment] = useState<Segment>('queue');
    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role = myClinic?.member?.role ?? null;
    const canEditCatalogue = role === 'doctor' || role === 'admin' || role === 'provider';

    return (
        <div className="space-y-4 max-w-4xl">
            <div>
                <h1 className="text-slate-900 text-2xl font-semibold">Procedures</h1>
                <div className="text-slate-500 text-sm mt-1">
                    Doctor-ordered procedures across the clinic and the catalogue of
                    procedures this clinic offers.
                </div>
            </div>

            <div className="pm-card p-1 inline-flex items-center gap-1">
                <SegmentButton active={segment === 'queue'} onClick={() => setSegment('queue')}>
                    Queue
                </SegmentButton>
                <SegmentButton active={segment === 'catalogue'} onClick={() => setSegment('catalogue')}>
                    Catalogue
                </SegmentButton>
            </div>

            {segment === 'queue' && <ProcedureQueueSection />}
            {segment === 'catalogue' && <ProcedureCatalogueSection canEdit={!!canEditCatalogue} />}
        </div>
    );
}

function SegmentButton(
    { active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode },
) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                active
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
        >
            {children}
        </button>
    );
}

// ─── Queue ───────────────────────────────────────────────────────────────

function ProcedureQueueSection() {
    const [hideMine, setHideMine] = useState(() => readFlag('procedures-hide-mine'));
    const [showClosed, setShowClosed] = useState(() => readFlag('procedures-show-closed'));

    function toggleHideMine(v: boolean) {
        setHideMine(v);
        writeFlag('procedures-hide-mine', v);
    }
    function toggleShowClosed(v: boolean) {
        setShowClosed(v);
        writeFlag('procedures-show-closed', v);
    }

    const { data, isLoading, isError } = useQuery({
        queryKey: ['procedures-queue', { hideMine, showClosed }],
        queryFn: () => proceduresApi.queue({
            hideMine,
            includeClosed: showClosed,
        }),
        refetchInterval: 15_000,
    });

    return (
        <div className="space-y-3">
            <div className="pm-card p-3 flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={hideMine}
                        onChange={(e) => toggleHideMine(e.target.checked)}
                    />
                    Hide mine
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={showClosed}
                        onChange={(e) => toggleShowClosed(e.target.checked)}
                    />
                    Show closed
                </label>
            </div>

            {isLoading && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    Loading procedures…
                </div>
            )}

            {isError && (
                <div className="pm-card p-6 text-slate-700 text-sm">
                    Could not load the procedures queue.
                </div>
            )}

            {data && data.entries.length === 0 && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    No open procedures right now.
                </div>
            )}

            {data && data.entries.length > 0 && (
                <ul className="space-y-2">
                    {data.entries.map((e) => (
                        <li key={e.service_request_id}>
                            <ProcedureRow entry={e} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function readFlag(key: string): boolean {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(key) === '1'; }
    catch { return false; }
}
function writeFlag(key: string, v: boolean): void {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(key, v ? '1' : '0'); }
    catch { /* storage quota or private mode — ignore, UI still works */ }
}

function ProcedureRow({ entry }: { entry: ProceduresQueueEntry }) {
    const acuityKey = entry.acuity ?? 'none';
    const name = entry.patient_full_name ?? entry.patient_phone_e164 ?? 'Unknown patient';
    return (
        <Link
            to={`/encounters/${entry.encounter_id}`}
            className="pm-card p-4 flex items-start justify-between gap-3 hover:bg-slate-50 transition"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${ACUITY_CLASS[acuityKey]}`}>
                        {ACUITY_LABEL[acuityKey]}
                    </span>
                    <span className="text-slate-900 font-medium text-sm truncate">
                        {name}
                    </span>
                    {entry.hospital_number && (
                        <span className="text-slate-400 text-xs">#{entry.hospital_number}</span>
                    )}
                </div>
                <div className="text-slate-800 text-sm mt-1">{entry.description}</div>
                <div className="text-slate-500 text-xs mt-1 flex items-center gap-2 flex-wrap">
                    <span>{STATUS_LABEL[entry.status]}</span>
                    <span aria-hidden>·</span>
                    <span>Requested {new Date(entry.requested_at).toLocaleString()}</span>
                    {entry.requested_by_full_name && (
                        <>
                            <span aria-hidden>·</span>
                            <span>by {entry.requested_by_full_name}</span>
                        </>
                    )}
                    {entry.fulfillment_count > 0 && (
                        <>
                            <span aria-hidden>·</span>
                            <span>{entry.fulfillment_count} session{entry.fulfillment_count === 1 ? '' : 's'} logged</span>
                        </>
                    )}
                </div>
            </div>
            <div className="text-brand-700 text-sm font-medium shrink-0">
                Open →
            </div>
        </Link>
    );
}

// ─── Catalogue ──────────────────────────────────────────────────────────

function ProcedureCatalogueSection({ canEdit }: { canEdit: boolean }) {
    const [editing, setEditing] = useState<ClinicProcedure | 'new' | null>(null);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['procedure-catalogue'],
        queryFn: () => procedureCatalogueApi.list({ includeInactive: true }),
    });

    return (
        <div className="space-y-3">
            {canEdit && (
                <div>
                    <button className="pm-btn-primary text-sm" onClick={() => setEditing('new')}>
                        + Add procedure
                    </button>
                </div>
            )}
            {isLoading && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    Loading catalogue…
                </div>
            )}
            {isError && (
                <div className="pm-card p-6 text-slate-700 text-sm">
                    Could not load the catalogue.
                </div>
            )}
            {data && data.procedures.length === 0 && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    No procedures in this catalogue yet.
                </div>
            )}
            {data && data.procedures.length > 0 && (
                <ul className="space-y-2">
                    {data.procedures.map((p) => (
                        <li key={p.id}>
                            <CatalogueRow procedure={p} canEdit={canEdit} onEdit={() => setEditing(p)} />
                        </li>
                    ))}
                </ul>
            )}
            {editing && (
                <CatalogueEditor
                    initial={editing === 'new' ? null : editing}
                    onDone={() => setEditing(null)}
                />
            )}
        </div>
    );
}

function CatalogueRow(
    { procedure, canEdit, onEdit }: { procedure: ClinicProcedure; canEdit: boolean; onEdit: () => void },
) {
    const qc = useQueryClient();
    const del = useMutation({
        mutationFn: () => procedureCatalogueApi.delete(procedure.id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['procedure-catalogue'] }),
    });

    const price =
        procedure.price_kobo == null
            ? '—'
            : `₦${(procedure.price_kobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
    const priceLabel = procedure.is_placeholder_price ? `${price} (placeholder)` : price;

    return (
        <div className="pm-card p-4 flex items-start justify-between gap-3">
            <div>
                <div className="font-medium text-slate-900">
                    {procedure.name}
                    <span className="ml-2 text-xs font-normal text-slate-500">({procedure.code})</span>
                    {!procedure.active && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-500">
                            Inactive
                        </span>
                    )}
                </div>
                <div className="text-sm text-slate-600 flex items-center gap-3 flex-wrap">
                    <span>{priceLabel}</span>
                    {procedure.category && <span>· {procedure.category}</span>}
                    {procedure.duration_minutes != null && (
                        <span>· {procedure.duration_minutes} min</span>
                    )}
                </div>
            </div>
            {canEdit && (
                <div className="flex items-center gap-2">
                    <button className="pm-btn-ghost text-sm" onClick={onEdit}>
                        Edit
                    </button>
                    <button
                        className="pm-btn-ghost text-sm text-rose-600"
                        onClick={() => {
                            if (window.confirm(`Delete "${procedure.name}"?`)) del.mutate();
                        }}
                        disabled={del.isPending}
                    >
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
}

function CatalogueEditor({ initial, onDone }: { initial: ClinicProcedure | null; onDone: () => void }) {
    const [code, setCode] = useState(initial?.code ?? '');
    const [name, setName] = useState(initial?.name ?? '');
    const [category, setCategory] = useState(initial?.category ?? '');
    const [duration, setDuration] = useState(initial?.duration_minutes?.toString() ?? '');
    const [priceNaira, setPriceNaira] = useState(
        initial?.price_kobo != null ? (initial.price_kobo / 100).toString() : '',
    );
    const [active, setActive] = useState(initial?.active ?? true);
    const [error, setError] = useState<string | null>(null);

    const qc = useQueryClient();
    const save = useMutation({
        mutationFn: (body: UpsertClinicProcedureRequest) => procedureCatalogueApi.upsert(body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['procedure-catalogue'] });
            onDone();
        },
        onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Save failed'),
    });

    function submit() {
        setError(null);
        if (!code.trim() || !name.trim()) {
            setError('Code and name are required');
            return;
        }
        const priceKobo = priceNaira.trim() === '' ? null : Math.round(Number(priceNaira) * 100);
        if (priceKobo != null && (!Number.isFinite(priceKobo) || priceKobo < 0)) {
            setError('Invalid price');
            return;
        }
        const durationMinutes = duration.trim() === '' ? null : Number(duration);
        if (durationMinutes != null && (!Number.isInteger(durationMinutes) || durationMinutes < 0)) {
            setError('Invalid duration');
            return;
        }
        save.mutate({
            ...(initial ? { id: initial.id } : {}),
            code: code.trim(),
            name: name.trim(),
            category: category.trim() || null,
            duration_minutes: durationMinutes,
            price_kobo: priceKobo,
            is_placeholder_price: priceKobo == null,
            active,
        });
    }

    return (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50">
            <div className="pm-card p-6 w-full max-w-md space-y-3">
                <h2 className="text-lg font-semibold text-slate-900">
                    {initial ? 'Edit procedure' : 'Add procedure'}
                </h2>
                <Field label="Code">
                    <input
                        className="pm-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="iv-cannulation"
                    />
                </Field>
                <Field label="Name">
                    <input
                        className="pm-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="IV cannulation"
                    />
                </Field>
                <Field label="Category">
                    <input
                        className="pm-input"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="Minor nursing"
                    />
                </Field>
                <Field label="Duration (minutes)">
                    <input
                        className="pm-input"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        placeholder="10"
                        inputMode="numeric"
                    />
                </Field>
                <Field label="Price (₦)">
                    <input
                        className="pm-input"
                        value={priceNaira}
                        onChange={(e) => setPriceNaira(e.target.value)}
                        placeholder="Leave blank to flag as unpriced"
                        inputMode="decimal"
                    />
                </Field>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                    />
                    Active (orderable)
                </label>
                {error && <div className="text-sm text-rose-600">{error}</div>}
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button className="pm-btn-ghost" onClick={onDone} disabled={save.isPending}>
                        Cancel
                    </button>
                    <button className="pm-btn-primary" onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="block space-y-1">
            <span className="text-xs font-semibold text-slate-600">{label}</span>
            {children}
        </label>
    );
}
